import axios from "axios";
import { environment } from "src/environments/environment";
import { find } from 'underscore'
import { Network } from "@capacitor/network";
import { BlobServiceClient, BlockBlobClient, ContainerClient } from "@azure/storage-blob";
import { Directory, FileInfo, Filesystem} from "@capacitor/filesystem";

interface File {
    name: string,
    url: string,
    path: string,
    mime_type: string,
    last_modified?: string
}

interface Folder {
    name: string,
}

const mime = require('mime-types')

export class MiscHelper {
    static sleep(ms) {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    static getAssetUrl(path: string) {
        let url = `${environment.assets_url}${path}`;
        if (environment.is_online) {
            let params = this.getCookieValue('storage_token');
            let extension = params.replace(/"/g, "");
            url = `${url}?${extension}`;
        }
        return url;
    }

    /**
     * Get Container Client
     * @returns
     */
    static async getContainerClient() {
        const blobServiceUrl = environment.assets_url;
        const token = environment.storage_token
        return new ContainerClient(`${blobServiceUrl}?${token}`)
    }

    /**
     * Get BlockBlobClient
     * @param filename
     * @returns
     */
    static async getBlockBlobClient(filename: string) {
        const containerClient = this.getContainerClient()
        const blockBlobClient = (await containerClient).getBlockBlobClient(filename);
        return blockBlobClient;
    }

    /**
     * Helper function to read directory and filter by type
     * @param path
     * @param type
     * @returns
     */
    static async readDirectory(path: string, type: 'directory' | 'file') {
        let result = [];
        try {
            const { files } = await Filesystem.readdir({
                path: `repo/${path}`,
                directory: Directory.Data
            });
            console.log(files)
            if (files.length > 0) {
                result = files
                    .filter(item => item.type === type)
                    .map(item => type === 'directory' ? { name: item.name } : {
                        name: item.name,
                        url: item.uri,
                        path: path,
                        mime_type: this.getFileType(this.getMimeType(`repo/${path}/`, item.name))
                    });
            }
        } catch (error) {
            console.error(`Unable to read ${type}s in directory: ${path}`, error);
        }
        return result;
    }

    /**
     * Get list of folders and directories in the device
     * @param path
     * @returns
     */
    static async getLocalFolders(path: string) {
        const folders = await this.readDirectory(path, 'directory');
        console.log('Folders:', folders);
        return folders;
    }

    /**
     * Get list of files and directories in the device
     * @param path
     * @returns
     */
    static async getLocalFiles(path: string) {
        const files = await this.readDirectory(path, 'file');
        console.log('Files:', files);
        return files;
    }

    /**
     * Get list of files in the azure storage
     * @param path
     * @returns
     */
    static async getStorageFiles(path: string) {
        const _files: Array<File> = [];
        const containerClient = this.getContainerClient()
        try {
            let blobs = (await containerClient).listBlobsFlat({prefix: path});
            for await (const blob of blobs) {
                const tempBlockBlobClient: BlockBlobClient = (await containerClient).getBlockBlobClient(blob.name);
                const fileName = this.removePath(path, blob.name)
                const file: File = {
                    name: fileName,
                    url: tempBlockBlobClient.url,
                    path: path,
                    mime_type: this.getFileType(blob.properties.contentType),
                    last_modified: blob.versionId
                }
                _files.push(file)
            }
        } catch (error) {
            console.log(error.message);
        }
        return _files
    }

    /**
     * Get list of folders in the azure storage
     * @param path
     * @returns
     */
    static async getStorageFolders(path: string) {
        let _folders: Array<Folder> = [];
        const containerClient = this.getContainerClient()
        try {
            for await (const blob of (await containerClient).listBlobsByHierarchy('/', { prefix: `${path}/` })) {
                if (blob.kind === 'prefix') {
                    const folder: Folder = {
                        name: blob.name.replace(new RegExp(`^${path}/|/$`, 'g'), '')
                    }
                    _folders.push(folder)
                }
            }
        } catch (error) {
            console.log(error.message);
        }
        return _folders
    }
    
    /**
     * Delete folder and files in the azure storage
     * @param path
     * @param blobName
     * @param is_file
     * @returns
     */
    static async deleteBlobIfItExists(path: string, blobName: string, is_file?: boolean){
        const containerClient = this.getContainerClient()
        try {
            let blobs = (await containerClient).listBlobsFlat({prefix: path});
            for await (const blob of blobs) {
                const name = blob.name.replace(new RegExp(`^${path}/|/$`, 'g'), '')
                if (!is_file || (is_file && blobName === name)) {
                    const tempBlockBlobClient: BlockBlobClient = (await containerClient).getBlockBlobClient(blob.name);
                    const result = await tempBlockBlobClient.deleteIfExists();
                    console.log(`Deleted blob: ${name}`)
                }
            }
        } catch (error) {
            console.log(error.message);
        }
    }

    /**
     * Rename BLOB
     * @param sourceBlobName
     * @param destinationBlobName
     */
    static async renameBlob (sourceBlobName: string, destinationBlobName: string) {
        const containerClient = this.getContainerClient()
        const sourceBlobClient = (await containerClient).getBlobClient(sourceBlobName);
        const destinationBlobClient = (await containerClient).getBlobClient(destinationBlobName);
        const copyPoller = await destinationBlobClient.beginCopyFromURL(sourceBlobClient.url);
        await copyPoller.pollUntilDone();
        await sourceBlobClient.delete();
    }

    /**
     * Rename Folder
     * @param sourceFolderName
     * @param destinationFolderName
     */
    static async renameFolder(sourceFolderName: string, destinationFolderName: string) {
        const containerClient = this.getContainerClient()
        for await (const blob of (await containerClient).listBlobsFlat({ prefix: sourceFolderName })) {
          const sourceBlobName = blob.name;
          const destinationBlobName = sourceBlobName.replace(sourceFolderName, destinationFolderName);
          const sourceBlobClient = (await containerClient).getBlobClient(sourceBlobName);
          const destinationBlobClient = (await containerClient).getBlobClient(destinationBlobName);
          const copyPoller = await destinationBlobClient.beginCopyFromURL(sourceBlobClient.url);
          await copyPoller.pollUntilDone();
          await sourceBlobClient.delete();
        }
    }

    /**
     * Returns the file type base on mime type
     * @param type
     * @returns
     */
    static getFileType(type:string) {
        const mimeTypeMap = {
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
            'application/pdf': 'pdf',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'excel',
            'application/vnd.ms-excel': 'excel',
            'text/csv': 'csv'
        };
        if (type.startsWith('image/')) {
            return 'image';
        }
        return mimeTypeMap[type] || 'unknown';
    }

    static getMimeType(path: string, filename: string) {
        return mime.contentType(this.removePath(path, filename))
    }

    static removePath(path: string, blobName: string) {
        return blobName.replace(path.concat('/'), '')
    }

    static async getJson(path: string): Promise<any> {
        try {
            let url = `${environment.assets_url}${path}`;
            if (environment.is_online) {
                let params = this.getCookieValue('storage_token');
                let extension = params.replace(/"/g, "");
                url = `${url}?${extension}`;
            }
            let result = await axios.get(url, {
                headers: {
                    'Content-Type': 'application/json'
                }
            });
            return result.data;
        } catch (e) {
            console.log(`MiscHelper.getJson failed`, e);
            return null;
        }
    }

    static getCookieValue(name) {
        const cookiestring = RegExp(name + "=[^;]+").exec(document.cookie);
        return cookiestring.toString().replace(/^[^=]+./, "");
    }

    static getFileTypeFromMime(content_type: string) {
        const mappings = [
            {
                name: 'image',
                types: ['image/gif', 'image/jpeg', 'image/png', 'image/bmp']
            },
            {
                name: 'video',
                types: ['video/mpeg', 'video/mp4', 'video/quicktime', 'video/x-msvideo', 'video/x-sgi-movie', 'application/mp4']
            }
        ];

        const type = find(mappings, item => {
            return item.types.indexOf(content_type) > -1
        });
        if (type) {
            return type.name;
        }
    }

    static async blobToBase64(blob) {
        return new Promise((resolve, _) => {
            const reader = new FileReader();
            reader.onloadend = () => resolve(reader.result);
            reader.readAsDataURL(blob);
        });
    }

    static async isConnected(){
        const status = await Network.getStatus();
        return ['wifi', 'cellular'].indexOf(status.connectionType) > -1 && status.connected;
    }

    static groupArray(data: any[], group_length: number){
        const result = []
        let group_index = -1
        for(let index in data){
            const item = data[index]
            if(parseInt(index) % group_length === 0){
                group_index++
                result[group_index] = []
                
            }
            result[group_index].push(item)
        }
        return [...result]
    }

    static async getText(path: string): Promise<string> {
        try {
            let url = `${environment.assets_url}${path}`;
            if (environment.is_online) {
                let params = this.getCookieValue('storage_token');
                let extension = params.replace(/"/g, "");
                url = `${url}?${extension}`;
            }
            let result = await axios.get(url);
            return result.data;
        } catch (e) {
            console.log(`MiscHelper.getText failed`, e);
            return null;
        }
    }
}